import { TFunction } from 'i18next';
import { ImportFieldLabels } from './importNumericLimits';

/**
 * Widths of the string import columns, and the reading of the error the
 * backend answers when a value passes them anyway.
 *
 * Same defect class as WA-3899, other type: the import writes the whole file
 * in one transaction, so a value the column cannot hold costs the import, not
 * the row. An article number over the 10 characters of `ArtikelNr` aborted the
 * same operator's import five times in a row with a raw `Data too long for
 * column 'ArtikelNr' at row 1` (WA-3906) - the file kept being uploaded, and
 * the preview never said which cell was wrong.
 *
 * Unlike a number, a string is known to be too long the moment the file is
 * parsed, so the check runs on the preview as well: the rows are marked and
 * the Import button stays off until the values fit. The backend keeps the same
 * limits (`columnLimits.ts`) and answers `tooLongValues` for whatever still
 * gets through, which is read here too.
 *
 * The widths are the `nvarchar(n)` columns of `FakturaAssistArtikel` and
 * `FakturaAssistKunde`. TEXT columns (notes, e-mail, declaration) hold 64 KB
 * and are not listed.
 */

/** Characters (code points) the column holds. */
export type ImportStringLimits = Record<string, number>;

/** FakturaAssistArtikel. */
export const ARTICLE_IMPORT_STRING_LIMITS: ImportStringLimits = {
  articleNo: 10, // ArtikelNr nvarchar(10) - the field WA-3906 is about
  description: 64, // Bezeichnung nvarchar(64)
  eanCode: 32, // EANNr nvarchar(32)
};

/** FakturaAssistKunde. */
export const CUSTOMER_IMPORT_STRING_LIMITS: ImportStringLimits = {
  customerNo: 16, // KundenNr nvarchar(16)
  salutation: 40,
  name: 40,
  surname: 40,
  internalName: 60,
  addition: 60,
  street: 40,
  postBox: 40,
  zipCode: 12,
  city: 40,
  country: 20,
  contactPerson: 40,
  phoneBusiness: 40,
  phoneBusiness2: 40,
  phonePrivate: 40,
  phoneMobile: 40,
  fax: 40,
  website: 40,
  deliveryAddressSalutation: 40,
  deliveryAddressName: 40,
  deliveryAddressAddition: 40, // LieferscheinZusatz is narrower than Zusatz (60)
  deliveryAddressStreet: 40,
  deliveryAddressPostBox: 40,
  deliveryAddressZipCode: 12,
  deliveryAddressCity: 40,
  deliveryAddressCountry: 20,
  costCenter: 30,
  incomeAccount: 30,
  slipReferenceNo: 32,
  eBillAccount: 40,
  tourPreparationArea: 16,
  tourSatPreparationArea: 16,
  tourSunPreparationArea: 16,
  tourDepartureTime: 32,
  tourSatDepartureTime: 32,
  tourSunDepartureTime: 32,
  ...Object.fromEntries(
    [1, 2].flatMap((bank) => [
      [`bank${bank}Name`, 32],
      [`bank${bank}BIN`, 16],
      [`bank${bank}AccountNo`, 32],
      [`bank${bank}IBAN`, 32],
      [`bank${bank}BIC`, 32],
      [`bank${bank}MandatNo`, 32],
    ]),
  ),
};

/** One refused string, as the preview finds it or `assertImportValuesFitColumns` reports it. */
export interface TooLongValue {
  /** `articleNo` / `customerNo` of the row it sits in. */
  recordNo: string;
  field: string;
  /** DB column - only sent by the backend, only for support, never shown. */
  column?: string;
  /** The value, or its beginning: a memo does not belong in an alert. */
  value: string;
  length: number;
  max: number;
}

/** Characters of a refused value the alert repeats. */
const VALUE_PREVIEW = 40;

/**
 * MySQL counts an `nvarchar(n)` in characters, and so does `Array.from`; the
 * UTF-16 `.length` would refuse a name with an emoji or a CJK pair that fits.
 */
export const characterCount = (value: string): number => Array.from(value).length;

/** The strings of the parsed rows that will not fit - what the Import button is held back for. */
export const collectTooLongValues = <T extends object>(
  rows: ReadonlyArray<T>,
  limits: ImportStringLimits,
  recordNoField: string,
): TooLongValue[] => {
  const values: TooLongValue[] = [];

  for (const item of rows) {
    const row = item as Record<string, unknown>;
    for (const [field, max] of Object.entries(limits)) {
      const value = row[field];

      if (typeof value !== 'string') {
        continue;
      }

      const length = characterCount(value);

      if (length <= max) {
        continue;
      }

      values.push({
        recordNo: String(row[recordNoField] ?? ''),
        field,
        value: Array.from(value).slice(0, VALUE_PREVIEW).join(''),
        length,
        max,
      });
    }
  }

  return values;
};

/**
 * The backend sends the refused strings in `linkedRecords.tooLongValues`, next
 * to the numeric `outOfRangeValues`. Anything else that arrives there is not
 * this error and is left to the generic handling.
 */
export const parseTooLongValues = (linkedRecords: unknown): TooLongValue[] => {
  const values = (linkedRecords as { tooLongValues?: unknown } | undefined)?.tooLongValues;

  if (!Array.isArray(values)) {
    return [];
  }

  return values.filter(
    (value): value is TooLongValue =>
      Boolean(value) &&
      typeof value.field === 'string' &&
      typeof value.value === 'string' &&
      typeof value.max === 'number',
  );
};

const key = ({ recordNo, field }: TooLongValue): string => `${recordNo}|${field}`;

/**
 * What the preview found plus what the backend answered, one entry per row and
 * field. The backend's copy matters when the limits here lag behind a column
 * change; the preview's copy is what disappears as soon as the cell is fixed.
 */
export const mergeTooLongValues = (
  local: TooLongValue[],
  fromServer: TooLongValue[],
): TooLongValue[] => {
  const seen = new Set(local.map(key));

  return [...local, ...fromServer.filter((value) => !seen.has(key(value)))];
};

/** How many values the alert spells out before it starts counting. */
const LISTED_VALUES = 3;

/**
 * `Artikel-Nr. = "12345678901" ("12345678901", 11 > max. 10)` - the field as it
 * is labelled in the grid, the value that was refused, the row it is in, its
 * length and the width of the column.
 */
export const formatTooLongDetails = (
  values: TooLongValue[],
  labels: ImportFieldLabels,
  t: TFunction,
): string => {
  const listed = values
    .slice(0, LISTED_VALUES)
    .map((value) => {
      const label = labels[value.field]?.(t) ?? value.field;
      const recordNo = value.recordNo ? `"${value.recordNo}", ` : '';
      return `${label} = "${value.value}" (${recordNo}${value.length} > max. ${value.max})`;
    })
    .join('; ');
  const rest = values.length - LISTED_VALUES;

  return rest > 0 ? `${listed}; +${rest}` : listed;
};

/** Rows a too-long value was found in, for the red row marking. */
export const tooLongRecordNos = (values: TooLongValue[]): Set<string> =>
  new Set(values.map(({ recordNo }) => recordNo).filter(Boolean));
